import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import i18n from '~/utils/i18n';

import { queryKeysDeliveryNote } from './queryKeys';

/**
 * Fetches the completed PDF download for the provided request ID.
 * Will poll the endpoint until the PDF is ready or timeout is reached.
 * On timeout, displays a warning toast and returns null.
 *
 * @param {string} requestId - The ID of the export request.
 * @param {number} maximumWaitingTime - Maximum time to wait in milliseconds (default: 5 minutes).
 * @returns {Promise<Blob | null>} The exported PDF or ZIP file as a Blob, or null if timeout occurs.
 * @throws {Error} If the PDF export fails for reasons other than timeout.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/get_delivery_note_pdf_export_asset_delivery_note_export_pdf__request_id__get
 */
export const fetchExportedDeliveryNotePdf = async (
  requestId: string,
  maximumWaitingTime = ms('5m'),
) => {
  let data = null;
  let attempts = 0;
  let timedOut = false;
  const retryDelay = ms('5s');

  const maxAttempts = maximumWaitingTime / retryDelay;

  while (!data && attempts < maxAttempts) {
    const response = await vestigasApi.get(
      ENDPOINT.DELIVERY_NOTE.GET_PDF_EXPORT(requestId),
    );

    // If status is 202, PDF is still being generated
    if (response.status === 202) {
      attempts++;
      if (attempts >= maxAttempts) {
        ToastService.warning(
          i18n.t(($) => $.pdfDownload.exportTimeout, { ns: 'delivery' }),
        );
        timedOut = true;
        break;
      }

      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      continue;
    }

    if (response.status === 200) {
      data = await response.blob();
      break;
    }
  }

  if (!data && !timedOut) {
    throw new Error('Failed to fetch exported PDF');
  }

  return data;
};

/**
 * React Query custom hook for fetching the exported delivery note PDF using request ID.
 *
 * @param {string} requestId - The ID of the export request.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult<Blob | null>} The result of the useQuery hook containing the exported delivery note PDF.
 */
export const useQueryDeliveryNotePdfExport = (
  requestId: string,
  options?: Omit<UseQueryOptions<Blob | null>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    enabled: Boolean(requestId),
    queryFn: async () => fetchExportedDeliveryNotePdf(requestId),
    queryKey: queryKeysDeliveryNote.getPdfExport(requestId),
    ...options,
  });
};
